import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Scale, Loader2, X, TrendingUp, TrendingDown, CheckCircle2, ArrowRight } from "lucide-react";

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const VERDICT = {
  overpriced: { label: "Overpriced", cls: "bg-orange-500/15 text-orange-500 border-orange-500/40", Icon: TrendingUp },
  underpriced: { label: "Underpriced", cls: "bg-sky-500/15 text-sky-500 border-sky-500/40", Icon: TrendingDown },
  competitive: { label: "Competitive", cls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40", Icon: CheckCircle2 },
};

const STATUS = {
  over: { cls: "text-orange-500", label: "over" },
  under: { cls: "text-sky-500", label: "under" },
  ok: { cls: "text-emerald-500", label: "on market" },
};

export const PriceCheck = ({ projectId, dirty, onSave, onMatched }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [matching, setMatching] = useState(false);

  const { data: pc } = useQuery({
    queryKey: ["price-check", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/price-check`)).data,
  });
  const result = pc && pc.verdict ? pc : null;

  const run = async () => {
    setRunning(true);
    try {
      if (dirty && onSave) await onSave();
      const { data } = await api.post(`/projects/${projectId}/price-check`);
      qc.setQueryData(["price-check", projectId], data);
      setOpen(true);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRunning(false); }
  };

  const match = async () => {
    setMatching(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/price-check/match`);
      toast.success(`Quote price-matched: ${money(data.old_total)} → ${money(data.grand_total)}`);
      if (onMatched) await onMatched();
      qc.invalidateQueries({ queryKey: ["price-check", projectId] });
      setOpen(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setMatching(false); }
  };

  const v = result ? VERDICT[result.verdict] || VERDICT.competitive : null;

  return (
    <>
      <div className="inline-flex items-center gap-2">
        <button
          data-testid="price-check-btn"
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/20 transition-colors disabled:opacity-60"
        >
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Scale className="h-3.5 w-3.5" />}
          {running ? "Checking pricing…" : "Price Check"}
        </button>
        {result && v && (
          <button
            data-testid="price-check-verdict-chip"
            onClick={() => setOpen(true)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-80 ${v.cls}`}
          >
            <v.Icon className="h-3.5 w-3.5" />
            {v.label} · {result.delta_pct > 0 ? "+" : ""}{Math.round(result.delta_pct)}%
          </button>
        )}
      </div>

      {open && result && v && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div
            data-testid="price-check-dialog"
            className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-2xl max-h-[85vh] overflow-auto animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-card">
              <span className="flex items-center gap-2 font-bold"><Scale className="h-4 w-4 text-primary" /> Price Comparison</span>
              <button data-testid="price-check-close" onClick={() => setOpen(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-secondary"><X className="h-4 w-4" /></button>
            </div>

            <div className="p-5 space-y-5">
              <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${v.cls}`}>
                <v.Icon className="h-6 w-6" />
                <div>
                  <div className="font-bold text-base">{v.label}</div>
                  <div className="text-xs opacity-90">Your bid is {result.delta_pct > 0 ? "+" : ""}{Math.round(result.delta_pct)}% vs expected market</div>
                </div>
              </div>

              {result.summary && <p className="text-sm text-muted-foreground leading-relaxed">{result.summary}</p>}

              {/* Market range */}
              <div className="rounded-xl border border-border p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Your bid vs fair local market range</div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><div className="text-xs text-muted-foreground">Your bid</div><div className="font-bold" data-testid="pc-your-total">{money(result.your_total)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Low</div><div className="font-semibold text-sky-500">{money(result.market_low)}</div></div>
                  <div><div className="text-xs text-muted-foreground">Expected</div><div className="font-semibold text-emerald-500">{money(result.market_expected)}</div></div>
                  <div><div className="text-xs text-muted-foreground">High</div><div className="font-semibold text-orange-500">{money(result.market_high)}</div></div>
                </div>
              </div>

              {/* Local labor / material / equipment comparison */}
              {result.cost_breakdown && (
                <div className="rounded-xl border border-border p-4" data-testid="pc-cost-breakdown">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Local market rate comparison</div>
                  <div className="space-y-1.5">
                    {[["Labor", "labor"], ["Materials", "material"], ["Equipment", "equipment"]].map(([label, k]) => {
                      const c = result.cost_breakdown[k];
                      if (!c) return null;
                      const st = STATUS[c.status] || STATUS.ok;
                      return (
                        <div key={k} data-testid={`pc-breakdown-${k}`} className="flex items-center justify-between gap-3 rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                          <span>{label}</span>
                          <span className="flex items-center gap-2 shrink-0 font-mono text-xs">
                            <span className="text-muted-foreground">{money(c.yours)}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className={st.cls}>{money(c.market)}</span>
                            <span className={`w-16 text-right ${st.cls}`}>{st.label}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2">Your labor wages, material prices &amp; equipment costs vs current local market rates.</p>
                </div>
              )}

              {/* Per-section */}
              {result.sections?.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">By division</div>
                  <div className="space-y-1.5">
                    {result.sections.map((s) => {
                      const st = STATUS[s.status] || STATUS.ok;
                      return (
                        <div key={s.code} data-testid={`pc-section-${s.code}`} className="flex items-center justify-between gap-3 rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                          <span className="truncate">{s.code} {s.title}</span>
                          <span className="flex items-center gap-2 shrink-0 font-mono text-xs">
                            <span className="text-muted-foreground">{money(s.your_subtotal)}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className={st.cls}>{money(s.market_subtotal)}</span>
                            <span className={`w-16 text-right ${st.cls}`}>{st.label}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {result.rate_sources?.length > 0 && (
                <div data-testid="pc-sources">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Local market sources</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {result.rate_sources.map((s, i) => (
                      <a key={i} href={s.url} target="_blank" rel="noreferrer" data-testid={`pc-source-${i}`} className="text-[11px] text-primary hover:underline truncate max-w-[200px]">↗ {s.label || s.url}</a>
                    ))}
                  </div>
                </div>
              )}

              <button
                data-testid="price-match-btn"
                onClick={match}
                disabled={matching}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-semibold py-3 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {matching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}
                Adjust quote to price-match market
              </button>
              <p className="text-[11px] text-center text-muted-foreground -mt-2">Scales each division's unit rates to the market level. Quantities stay the same.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
